/**
 * Talking to the server.
 *
 * Every path is `/api/client/…` and carries no business in it — deliberately. The business
 * comes from the customer's own record on the server side, so there is nothing here a customer
 * could change to look at somebody else's.
 */

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/client${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      (body as { error?: string } | null)?.error ?? `http_${response.status}`,
      (body as { message?: string } | null)?.message ?? 'Une erreur est survenue.',
    );
  }
  return body as T;
}

export type Business = { customerId: string; name: string; brandColor: string; displayName: string };

export type Session = {
  email: string | null;
  phone: string | null;
  active: { customerId: string; displayName: string; name: string; brandColor: string; locale: 'fr' | 'en' };
  businesses: Business[];
};

export type Visit = {
  id: string;
  number: string | null;
  state: 'scheduled' | 'en_route' | 'delayed' | 'arrived' | 'working' | 'done' | 'not_done' | 'cancelled';
  startsAt: string | null;
  endsAt: string | null;
  precision: 'exact' | 'period' | 'day';
  period: 'am' | 'pm' | 'day' | 'evening' | 'week' | null;
  etaAt: string | null;
  arrivedAt: string | null;
  completedAt: string | null;
  outcome: string | null;
  technician: string | null;
  address: { line1: string; line2: string | null; city: string; postalCode: string | null } | null;
  services: Array<Record<string, string>>;
  jobNumber: string;
  title: string | null;
  description: string | null;
  rescheduleRequestId: string | null;
};

export type Message = {
  id: string;
  authorKind: 'staff' | 'customer' | 'system';
  authorName: string | null;
  body: string | null;
  sentAt: string;
  attachments: Array<{ id: string; contentType: string }>;
};

export type Invoice = {
  id: string;
  number: string;
  status: string;
  issuedOn: string | null;
  dueOn: string | null;
  total: string;
  paidTotal: string;
  balance: string;
  note: string | null;
  items: Array<{ name: string; quantity: number; total: string }>;
  paymentToken: string | null;
};

export type Me = {
  business: { name: string; brandColor: string };
  me: { customerId: string; displayName: string; locale: 'fr' | 'en' };
  addresses: string[];
  visits: Visit[];
  messages: Message[];
  invoices: Invoice[];
  quotes: Array<{ id: string; number: string; status: string; total: string; expiresOn: string | null }>;
  notifications: Array<{ id: string; kind: string; title: string; body: string | null; readAt: string | null; at: string }>;
  preferences: { notifyOnRoute: boolean; notifyInvoice: boolean; notifyMessage: boolean; channel: 'email' | 'sms' };
};

export const api = {
  session: () => call<Session>('/auth/session'),
  requestCode: (destination: string) =>
    call<{ ok: true; expiresInMinutes: number }>('/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ destination }),
    }),
  verify: (destination: string, code: string) =>
    call<{ businesses: Business[]; activeCustomerId: string }>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ destination, code }),
    }),
  chooseBusiness: (customerId: string) =>
    call<{ ok: true }>('/auth/business', { method: 'POST', body: JSON.stringify({ customerId }) }),
  logout: () => call<{ ok: true }>('/auth/logout', { method: 'POST' }),

  me: () => call<Me>('/me'),
  send: (body: string, attachmentIds: string[]) =>
    call<{ id: string }>('/messages', { method: 'POST', body: JSON.stringify({ body, attachmentIds }) }),
  reschedule: (
    visitId: string,
    input: { preferredFrom?: string | null; preferredTo?: string | null; preferredPeriod?: string | null; note?: string | null },
  ) => call<{ id: string }>(`/visits/${visitId}/reschedule`, { method: 'POST', body: JSON.stringify(input) }),
  cancelReschedule: (visitId: string) => call<void>(`/visits/${visitId}/reschedule`, { method: 'DELETE' }),
  preferences: (input: Partial<Me['preferences']>) =>
    call<{ ok: true }>('/preferences', { method: 'PATCH', body: JSON.stringify(input) }),
  markRead: () => call<{ ok: true }>('/notifications/read', { method: 'POST' }),

  /** One photograph, one request. See the note on the endpoint for why it is not resumable. */
  upload: async (file: File): Promise<string> => {
    const response = await fetch('/api/client/uploads', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/octet-stream', 'x-content-type': file.type },
      body: file,
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new ApiError(response.status, (body as any)?.error ?? 'upload_failed', 'Cette photo n’a pas pu être envoyée.');
    }
    return (body as { attachmentId: string }).attachmentId;
  },
};

/** A `Translatable` from the catalogue, in the customer's own language. */
export function text(value: unknown, locale: 'fr' | 'en' = 'fr'): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const record = value as Record<string, string | undefined>;
    return record[locale] ?? record.fr ?? record.en ?? '';
  }
  return '';
}
